// Phase 8.PH.FINAL — Premium PDF / CSV export menu.
//
// Rendered inside the Portfolio action row. Only visible when the
// caller passes `visible === true` (parent computes this from the
// authenticated user's `locked` flag — Free / Pro / anon users must
// NOT see the export controls at all). The backend still enforces
// Premium via `/api/portfolio/export/*` returning 403 for anyone else,
// so the client-side visibility is a UX affordance, not a security
// boundary.
//
// Files are streamed as attachment responses; we use `fetch` with
// `credentials: "include"` so the JWT / session cookie is sent, and
// then trigger a download via a temporary object URL. The 403
// contract from the backend is surfaced as a toast (never silently
// consumed).
import React, { useState } from "react";
import { Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useTranslation } from "@/lib/i18n";

function _todayIso() {
  const d = new Date();
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

async function _downloadExport({ path, mime, filename, onError, onSuccess }) {
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const url = `${base}${path}`;
  const res = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: { Accept: mime },
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      msg = j?.detail?.message || j?.message || msg;
    } catch { /* body may not be JSON */ }
    onError?.(res.status, msg);
    return;
  }
  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objUrl);
  onSuccess?.();
}

export default function PortfolioExportMenu({ visible = true, disabled = false }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(null);  // "pdf" | "csv" | null

  if (!visible) return null;

  const handleExport = async (kind) => {
    if (busy) return;
    setBusy(kind);
    const isPdf = kind === "pdf";
    await _downloadExport({
      path: isPdf ? "/api/portfolio/export/pdf" : "/api/portfolio/export/csv",
      mime: isPdf ? "application/pdf" : "text/csv",
      filename: isPdf
        ? `stockaily-portfolio-report-${_todayIso()}.pdf`
        : `stockaily-portfolio-export-${_todayIso()}.csv`,
      onError: (status, msg) => {
        if (status === 403) {
          toast.error(t("portfolio.export.premium_required") || "Portfolio export is a Premium feature.");
        } else {
          toast.error(msg || t("portfolio.export.failed") || "Export failed.");
        }
      },
      onSuccess: () => {
        toast.success(
          isPdf
            ? (t("portfolio.export.pdf_success") || "PDF report ready.")
            : (t("portfolio.export.csv_success") || "CSV export ready."),
        );
      },
    });
    setBusy(null);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          disabled={disabled || !!busy}
          data-testid="portfolio-export-menu-trigger"
          aria-label={t("portfolio.export.aria_label") || "Export portfolio"}
          title={t("portfolio.export.title_hint") || "Export PDF / CSV"}
          className="h-9 w-9 shrink-0 hover:border-[#3DEBFF]/40 hover:text-[#3DEBFF]"
        >
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          <span className="sr-only">{t("portfolio.export.aria_label") || "Export portfolio"}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px]">
        <DropdownMenuItem
          data-testid="portfolio-export-pdf-item"
          onClick={() => handleExport("pdf")}
          disabled={!!busy}
          className="cursor-pointer"
        >
          <FileText className="w-4 h-4 mr-2 text-[#3DEBFF]" />
          <div className="flex flex-col">
            <span className="text-sm">
              {t("portfolio.export.pdf_label") || "Export PDF report"}
            </span>
            <span className="text-[10px] text-muted-foreground">
              {t("portfolio.export.pdf_hint") || "Branded Portfolio Intelligence Report"}
            </span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="portfolio-export-csv-item"
          onClick={() => handleExport("csv")}
          disabled={!!busy}
          className="cursor-pointer"
        >
          <FileSpreadsheet className="w-4 h-4 mr-2 text-[#2BFF8A]" />
          <div className="flex flex-col">
            <span className="text-sm">
              {t("portfolio.export.csv_label") || "Export CSV (holdings)"}
            </span>
            <span className="text-[10px] text-muted-foreground">
              {t("portfolio.export.csv_hint") || "Machine-readable holdings snapshot"}
            </span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
